import s from './Modal.module.sass'

import React, { FC, memo, ReactNode } from 'react'

import { IconButton } from '@shared/ui/Actions/IconButton'
import { Title } from '@shared/ui/Typography/Title'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'

import IconClose from '@icons/icon--x-big.svg'
import IconCloseThin from '@icons/icon--x-thin.svg'
import { ModalView } from '@shared/ui/Overlays/ModalView/ModalView'
import { MediaImg } from '@shared/ui/Media/MediaImg'

interface ModalProps {
  size: 'XS' | 'S' | 'M' | 'L' | 'XL'
  mobileMode?: 'dropdown' | 'dropdown-image'
  mobileShadowless?: boolean
  dropdownImage?: string
  title?: ReactNode
  subtitle?: ReactNode
  titleSizes?: string
  handleClose: () => void
  buttonClose?: boolean
  children: ReactNode
  className?: string
}

const Modal: FC<ModalProps> = ({
  size,
  mobileMode = 'dropdown',
  mobileShadowless,
  dropdownImage,
  title = '',
  subtitle,
  titleSizes = 'h2 h3',
  className = '',
  handleClose,
  buttonClose = true,
  children,
}) => {
  const { isMobile } = useClientResponsive()

  const isShowButtonCloseNearTitle = isMobile ? mobileMode === 'dropdown' && buttonClose : buttonClose

  return (
    <ModalView handleClose={handleClose}>
      <div className={`${s.inner} ${isMobile ? s[`mobile-mode--${mobileMode}`] : s[`desktop-size-${size}`]}`}>
        {mobileMode === 'dropdown-image' && isMobile && (
          <>
            <div className={s['dropdown-image']}>
              {/*@ts-ignore*/}
              <MediaImg
                image={{
                  Url: dropdownImage,
                  Width: 760,
                  Height: 370,
                  Crop: null,
                  Description: '',
                  Copyright: null,
                  Author: null,
                }}
                width={760}
                height={370}
                flexible={true}
                className={s.image}
              />
            </div>

            {handleClose && (
              <IconButton
                data-testid={'modal-button-close'}
                id={s['button-close']}
                className={s['button-close']}
                icon={isMobile ? IconCloseThin : IconClose}
                mode={'black'}
                sizes='M'
                onClick={handleClose}
              />
            )}
          </>
        )}

        <div className={`${s.container} ${className}`} onClick={(e) => e.stopPropagation()}>
          {title ? (
            <div className={s.header}>
              <div className={s['header-top']}>
                <Title tag='h3' sizes={titleSizes} className={s['header-title']}>
                  {title}
                </Title>

                {isShowButtonCloseNearTitle && (
                  <IconButton
                    data-testid={'modal-button-close'}
                    id={s['button-close']}
                    className={`${s['button-close']} ${s['button-close--static']}`}
                    icon={isMobile ? IconCloseThin : IconClose}
                    mode={'white'}
                    sizes={'XL M'}
                    onClick={handleClose}
                  />
                )}
              </div>
              {subtitle && subtitle}
            </div>
          ) : (
            isShowButtonCloseNearTitle && (
              <IconButton
                data-testid={'modal-button-close'}
                id={s['button-close']}
                className={`${s['button-close']} ${s['button-close--absolute']}`}
                icon={IconClose}
                mode={'white'}
                sizes={'XL M'}
                onClick={handleClose}
              />
            )
          )}

          <div className={s.content} data-content>
            <div className={`${mobileShadowless || s['content-shadow']} ${s['top']}`} data-shadow-top></div>
            <div className={s['content-inner']} data-content-inner>
              {children}
            </div>
            <div className={`${mobileShadowless || s['content-shadow']} ${s['bottom']}`} data-shadow-bottom></div>
          </div>
        </div>
      </div>
    </ModalView>
  )
}

export default memo(Modal)
